import React from 'react'
import { useTranslation } from 'react-i18next'
import { ClipboardCheck, Layers, FileText, Users, Shield, Lock, UserPlus } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Call4PapersModuleConfig, CfpWhoCanOpen, ModuleFieldComponentProps } from '@/types/space-wizard'

type Option<T> = {
  value: T
  icon: typeof FileText
  title: string
  description: string
}

/**
 * `settings` is the same fields on a space that already exists.
 *
 * It drops the call type, because in `single` mode the call is already created
 * and carries its own type — offering it again here would promise a change the
 * form cannot make. The wizard keeps it: there the call does not exist yet.
 */
type Variant = 'wizard' | 'settings'

export const Call4PapersModuleFields: React.FC<
  ModuleFieldComponentProps<Call4PapersModuleConfig> & { variant?: Variant }
> = ({ config, onChange, disabled = false, variant = 'wizard' }) => {
  const { t } = useTranslation(['common'])

  // Default: hub. It is the least restrictive mode — a `multi` space always
  // accommodates a single call, but a `single` closes the door to a second one.
  const spaceMode = config.spaceMode || 'multi'
  const callType = config.callType || 'simple'
  // The API's own default: the team opens calls unless the owner says otherwise.
  const whoCanOpenCall = config.whoCanOpenCall || 'staff'

  const typeOptions: Option<'simple' | 'advanced'>[] = [
    {
      value: 'simple',
      icon: FileText,
      title: t('modules.settings.call4papers.typeSimple'),
      description: t('modules.settings.call4papers.typeSimpleHint'),
    },
    {
      value: 'advanced',
      icon: Layers,
      title: t('modules.settings.call4papers.typeAdvanced'),
      description: t('modules.settings.call4papers.typeAdvancedHint'),
    },
  ]

  // 'multi' first: it is the default, and the default should be the first option.
  const modeOptions: Option<'single' | 'multi'>[] = [
    {
      value: 'multi',
      icon: Users,
      title: t('modules.settings.call4papers.modeMulti'),
      description: t('modules.settings.call4papers.modeMultiHint'),
    },
    {
      value: 'single',
      icon: ClipboardCheck,
      title: t('modules.settings.call4papers.modeSingle'),
      description: t('modules.settings.call4papers.modeSingleHint'),
    },
  ]

  // The default first (PADROES-UI §9), then from narrower to wider.
  const whoCanOpenOptions: Option<CfpWhoCanOpen>[] = [
    {
      value: 'staff',
      icon: Shield,
      title: t('modules.settings.call4papers.whoCanOpenStaff'),
      description: t('modules.settings.call4papers.whoCanOpenStaffHint'),
    },
    {
      value: 'owners',
      icon: Lock,
      title: t('modules.settings.call4papers.whoCanOpenOwners'),
      description: t('modules.settings.call4papers.whoCanOpenOwnersHint'),
    },
    {
      value: 'members',
      icon: UserPlus,
      title: t('modules.settings.call4papers.whoCanOpenMembers'),
      description: t('modules.settings.call4papers.whoCanOpenMembersHint'),
    },
  ]

  const renderGroup = <T extends string>(
    label: string,
    options: Option<T>[],
    selected: T,
    onSelect: (value: T) => void,
    testId?: string
  ) => (
    <div className="space-y-2" data-testid={testId}>
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className={cn('grid gap-2', options.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}>
        {options.map((option) => {
          const Icon = option.icon
          const isSelected = selected === option.value
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={isSelected}
              data-value={option.value}
              disabled={disabled}
              onClick={() => onSelect(option.value)}
              className={cn(
                'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                isSelected
                  ? 'border-primary bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              <Icon
                className={cn(
                  'mt-0.5 h-5 w-5 flex-shrink-0',
                  isSelected ? 'text-primary' : 'text-gray-400'
                )}
              />
              <span className="min-w-0">
                <span
                  className={cn(
                    'block text-sm font-medium',
                    isSelected ? 'text-primary-700' : 'text-gray-900'
                  )}
                >
                  {option.title}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                  {option.description}
                </span>
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <div className="space-y-5">
      {/* How many calls comes first: it decides whether the type is a space choice. */}
      {renderGroup(
        t('modules.settings.call4papers.spaceMode'),
        modeOptions,
        spaceMode,
        (value) =>
          onChange(
            value === 'multi'
              ? // In a hub each call picks its own type, so none is fixed here.
                { ...config, spaceMode: value, callType: undefined }
              : // A single-call space has its call made in the wizard; nobody
                // opens another, so "who opens" goes with the mode (§9).
                { ...config, spaceMode: value, whoCanOpenCall: undefined }
          )
      )}

      {spaceMode === 'multi' &&
        renderGroup(
          t('modules.settings.call4papers.whoCanOpen'),
          whoCanOpenOptions,
          whoCanOpenCall,
          (value) => onChange({ ...config, whoCanOpenCall: value }),
          'who-can-open-call'
        )}

      {/* Shown while editing too, not only in the wizard. The type belongs to
          the call rather than to the space, and the dialog that renders this in
          `settings` writes it to the call — so the control is the same question
          in both places instead of one that disappears after creation. */}
      {spaceMode === 'single' && (
        <div className="space-y-2">
          {renderGroup(
            t('modules.settings.call4papers.callType'),
            typeOptions,
            callType,
            (value) => onChange({ ...config, callType: value })
          )}
          {/* Said only while editing: in the wizard there is no call yet, so
              there is nothing for the change to reach. */}
          {variant === 'settings' && (
            <p className="text-xs text-gray-500">
              {t('modules.settings.call4papers.callTypeAppliesToCall')}
            </p>
          )}
        </div>
      )}

      <p className="rounded-md bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-500">
        {spaceMode === 'multi'
          ? t('modules.settings.call4papers.hintMulti')
          : variant === 'settings'
            /* "Next step: …" is the wizard talking. In an existing space there
               is no next step; what the reader needs is where the rest of the
               call is edited. */
            ? t('modules.settings.call4papers.hintSingleExisting')
            : callType === 'advanced'
              ? t('modules.settings.call4papers.hintSingleAdvanced')
              : t('modules.settings.call4papers.hintSingleSimple')}
      </p>
    </div>
  )
}
